import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
} from '@mui/material';
import { TableCellProps } from '@mui/material/TableCell';
import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  GetAssemblyPointOfInterestsQuery,
  PointOfInterest,
  UnitsEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { localiseMeasurement } from '@/utils/unitSystem';
import { getTemplateFieldValue } from '@/utils';
import { nominateButtonStyles, tableCellStyle } from '../styles';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { PointOfInterestDocument } from '@/types';
import { NOT_APPLICABLE_LABEL, NOMINATE_LABEL, NOMINATED_LABEL } from '@/constants';

const { primary: primaryColors } = abyssColors;

type Props = {
  allowedNominationPois: GetAssemblyPointOfInterestsQuery['allPointOfInterests'];
  unitSystem: UnitSystemEnum;
  governingPoiId?: string | undefined;
  userCanUpdateAsset?: boolean;
  handleNominate: (poi: PointOfInterest) => void;
  tableColumns?: Array<{ key?: string | null; displayName?: string | null } | null>;
};

type HeaderCol = {
  key: string;
  label: string;
  sx?: SxProps<Theme>;
  align?: TableCellProps['align'];
};

const AllowedNominationTable = ({
  allowedNominationPois,
  unitSystem,
  governingPoiId,
  userCanUpdateAsset,
  handleNominate,
  tableColumns,
}: Props) => {
  const allowedPois = allowedNominationPois ?? [];
  if (allowedPois.length === 0) return <></>;

  const leftHeaderCols: HeaderCol[] = [
    { key: 'id', label: 'ID', sx: { ...tableCellStyle, whiteSpace: 'nowrap' }, align: 'center' },
  ];

  const rightHeaderCols: HeaderCol[] = [
    {
      key: 'wallLoss',
      label: 'WALL LOSS',
      sx: { ...tableCellStyle, whiteSpace: 'nowrap' },
      align: 'right',
    },
    {
      key: 'governing',
      label: 'GOVERNING',
      sx: { ...tableCellStyle, width: '100%', textAlign: 'center' },
      align: 'center',
    },
  ];

  return (
    <Paper elevation={0} sx={{ padding: 1, width: '100%' }}>
      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table sx={{ borderCollapse: 'collapse', tableLayout: 'auto', minWidth: 300, mb: 1 }}>
          <TableHead>
            <TableRow>
              {leftHeaderCols.map((column) => (
                <TableCell key={column.key} sx={column.sx} align={column.align}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{column.label}</Typography>
                </TableCell>
              ))}

              {(tableColumns ?? []).map((col, idx) => (
                <TableCell
                  key={`head-field-${idx}`}
                  sx={{ ...tableCellStyle, whiteSpace: 'nowrap' }}
                  align="center"
                >
                  <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                    {col?.displayName ?? col?.key ?? ''}
                  </Typography>
                </TableCell>
              ))}

              {rightHeaderCols.map((column) => (
                <TableCell key={column.key} sx={column.sx} align={column.align}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {allowedPois.map((poi, idx) => {
              const displayWallLoss = poi.blister
                ? localiseMeasurement({
                    unitSystem,
                    value: poi.blister?.wallLoss?.value || 0,
                    sourceUnit: UnitsEnum.Meter,
                    displayAsUnit: UnitsEnum.Millimeter,
                    showUnitLabel: true,
                  })
                : NOT_APPLICABLE_LABEL;
              const poiDisplayName = generatePoiDisplayName({
                name: poi?.name,
                poiId: poi?.poiId,
                poiType: poi?.type,
              });
              const isNominated = governingPoiId ? poi.id === governingPoiId : false;
              const buttonStyles = isNominated
                ? {
                    color: abyssColors.secondary?.[800] || primaryColors[50],
                    backgroundColor: abyssColors.secondary?.[50] || abyssColors.primary[500],
                  }
                : { color: primaryColors[50], backgroundColor: abyssColors.primary[500] };

              return (
                <TableRow key={`allowed-${idx}`}>
                  <TableCell
                    sx={{
                      ...tableCellStyle,
                      cursor: 'pointer',
                      ':hover': { backgroundColor: abyssColors.primary[50] },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography sx={{ fontSize: 10, fontWeight: 400 }}>{poiDisplayName}</Typography>
                  </TableCell>

                  {(tableColumns ?? []).map((column, index) => {
                    const value = getTemplateFieldValue(
                      poi?.templateFieldData,
                      column?.key ?? null
                    );
                    const displayVal = value ? String(value) : NOT_APPLICABLE_LABEL;
                    return (
                      <TableCell
                        key={`val-${idx}-${index}`}
                        sx={{ ...tableCellStyle, whiteSpace: 'nowrap' }}
                        align="center"
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 400 }}>{displayVal}</Typography>
                      </TableCell>
                    );
                  })}

                  <TableCell sx={{ ...tableCellStyle, whiteSpace: 'nowrap' }} align="right">
                    <Typography sx={{ fontSize: 10, fontWeight: 400 }}>
                      {displayWallLoss || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...tableCellStyle, textAlign: 'center', width: '100%' }}>
                    {userCanUpdateAsset && (
                      <Button
                        data-testid={`nominate-${poi?.name}`}
                        onClick={() =>
                          !isNominated && handleNominate(poi as PointOfInterestDocument)
                        }
                        sx={{
                          ...containedButtonStyle,
                          ...nominateButtonStyles,
                          ...buttonStyles,
                          cursor: isNominated ? 'default' : 'pointer',
                          ':hover': { ...buttonStyles },
                        }}
                      >
                        {isNominated ? NOMINATED_LABEL : NOMINATE_LABEL}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AllowedNominationTable;
